import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  Select,
  Spinner,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { MdArrowBack, MdDelete, MdAdd } from "react-icons/md";
import { useNavigate, useParams } from "react-router-dom";
import { fetchController } from "../../utils/FetchController/fetchController";
import { getHostDomain } from "../../utils/utils";

export default function CalendarPoolEdit() {
  const baseUrl = getHostDomain();
  const navigate = useNavigate();
  const { poolId } = useParams();
  const toast = useToast();

  const [pool, setPool] = useState(null);
  const [members, setMembers] = useState([]);
  const [availableConnections, setAvailableConnections] = useState([]); // existing LinkToolUsers
  const [selectedConnection, setSelectedConnection] = useState("");
  const [status, setStatus] = useState("idle");
  const [saving, setSaving] = useState(false);

  // ── Load: pool, members, available google_calendar connections ─────────────

  const loadAll = useCallback(async () => {
    setStatus("loading");
    try {
      // 1) Pool details
      const poolRes = await fetchController(
        `${baseUrl}/api/v1/calendar_pool/${poolId}`,
        "GET"
      );
      const poolData = poolRes?.data;
      setPool(poolData);
      setMembers(poolData?.members || []);

      // 2) Find the Google Calendar system tool, then list this user's connections to it
      const toolsRes = await fetchController(
        `${baseUrl}/api/v1/tool/list?size=100`,
        "GET"
      );
      const tools = toolsRes?.data?.items || [];
      const gcal = tools.find((t) => t.tool_provider === "google_calendar" && t.tool_type === "system");
      if (gcal) {
        const ltuRes = await fetchController(
          `${baseUrl}/api/v1/tool/${gcal.id}/LinkToolUser?size=100`,
          "GET"
        );
        setAvailableConnections(ltuRes?.data?.items || []);
      }

      setStatus("success");
    } catch (e) {
      setStatus("error");
    }
  }, [baseUrl, poolId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Filter available connections to only those NOT already in the pool
  const memberIds = useMemo(() => new Set(members.map((m) => m.link_tool_user_id)), [members]);
  const addableConnections = useMemo(
    () => availableConnections.filter((c) => !memberIds.has(c.id)),
    [availableConnections, memberIds]
  );

  // ── Pool meta save ─────────────────────────────────────────────────────────

  async function savePool() {
    if (!pool) return;
    setSaving(true);
    try {
      const res = await fetchController(
        `${baseUrl}/api/v1/calendar_pool/${poolId}`,
        "PUT",
        {
          name: pool.name,
          description: pool.description,
          strategy: pool.strategy,
        }
      );
      setPool(res?.data || pool);
      toast({ title: "Pool saved", status: "success", duration: 2000 });
    } catch (e) {
      toast({ title: "Save failed", description: e?.message, status: "error" });
    } finally {
      setSaving(false);
    }
  }

  // ── Members ────────────────────────────────────────────────────────────────

  async function addMember() {
    if (!selectedConnection) return;
    try {
      await fetchController(
        `${baseUrl}/api/v1/calendar_pool/${poolId}/members`,
        "POST",
        {
          pool_id: poolId,
          link_tool_user_id: selectedConnection,
          priority: members.length,
          is_active: true,
        }
      );
      setSelectedConnection("");
      loadAll();
    } catch (e) {
      toast({ title: "Add member failed", description: e?.message, status: "error" });
    }
  }

  async function removeMember(member) {
    if (!window.confirm("Remove this member from the pool?")) return;
    try {
      await fetchController(
        `${baseUrl}/api/v1/calendar_pool/${poolId}/members/${member.id}`,
        "DELETE"
      );
      loadAll();
    } catch (e) {
      toast({ title: "Remove failed", description: e?.message, status: "error" });
    }
  }

  async function toggleMemberActive(member) {
    try {
      await fetchController(
        `${baseUrl}/api/v1/calendar_pool/${poolId}/members/${member.id}`,
        "PUT",
        { is_active: !member.is_active }
      );
      loadAll();
    } catch (e) {
      toast({ title: "Update failed", description: e?.message, status: "error" });
    }
  }

  async function updateMemberPriority(member, newPriority) {
    try {
      await fetchController(
        `${baseUrl}/api/v1/calendar_pool/${poolId}/members/${member.id}`,
        "PUT",
        { priority: newPriority }
      );
      loadAll();
    } catch (e) {
      toast({ title: "Priority update failed", description: e?.message, status: "error" });
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  if (status === "loading" || !pool) {
    return (
      <Flex justify="center" align="center" h="100%">
        <Spinner />
      </Flex>
    );
  }

  if (status === "error") {
    return (
      <Box p={6}>
        <Text color="red.500">Failed to load pool.</Text>
        <Button mt={4} onClick={() => navigate("/library/calendar-pools")}>
          ← Back to list
        </Button>
      </Box>
    );
  }

  return (
    <VStack align="stretch" spacing={4} h="100%">
      <Flex align="center" gap={3}>
        <IconButton
          icon={<MdArrowBack />}
          aria-label="Back"
          variant="ghost"
          onClick={() => navigate("/library/calendar-pools")}
        />
        <Heading size="md" flex="1">{pool.name}</Heading>
        <Button colorScheme="blue" onClick={savePool} isLoading={saving}>
          Save Pool Settings
        </Button>
      </Flex>

      {/* Pool settings */}
      <Card variant="elevated">
        <CardBody>
          <VStack align="stretch" spacing={4}>
            <FormControl>
              <FormLabel>Name</FormLabel>
              <Input
                value={pool.name || ""}
                onChange={(e) => setPool({ ...pool, name: e.target.value })}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                value={pool.description || ""}
                onChange={(e) => setPool({ ...pool, description: e.target.value })}
                rows={2}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Strategy</FormLabel>
              <Select
                value={pool.strategy}
                onChange={(e) => setPool({ ...pool, strategy: e.target.value })}
                maxW="320px"
              >
                <option value="round_robin">Round Robin</option>
                <option value="priority">Priority</option>
                <option value="least_busy">Least Busy</option>
              </Select>
              <Text fontSize="xs" color="gray.500" mt={1}>
                {pool.strategy === "round_robin" && "Rotates through active members in order."}
                {pool.strategy === "priority" && "Tries members in priority order (lower number first) and picks the first one with the slot free."}
                {pool.strategy === "least_busy" && "Picks the least-loaded member with the slot free."}
              </Text>
            </FormControl>
          </VStack>
        </CardBody>
      </Card>

      {/* Members */}
      <Card variant="elevated">
        <CardBody>
          <Flex align="center" justify="space-between" mb={4}>
            <Heading size="sm">Members</Heading>
            <Text fontSize="xs" color="gray.500">
              {members.length} member{members.length === 1 ? "" : "s"}
            </Text>
          </Flex>

          <HStack mb={4}>
            <Select
              placeholder={
                addableConnections.length === 0
                  ? "No more Google Calendar connections available"
                  : "Add an agent's Google Calendar connection..."
              }
              value={selectedConnection}
              onChange={(e) => setSelectedConnection(e.target.value)}
              isDisabled={addableConnections.length === 0}
              maxW="500px"
            >
              {addableConnections.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name || c.id}
                </option>
              ))}
            </Select>
            <Button
              leftIcon={<MdAdd />}
              colorScheme="blue"
              onClick={addMember}
              isDisabled={!selectedConnection}
            >
              Add
            </Button>
          </HStack>

          {members.length === 0 ? (
            <Box textAlign="center" py={6} color="gray.400" fontSize="sm">
              No members yet. Add a Google Calendar connection above.
            </Box>
          ) : (
            <Box overflowX="auto">
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th w="60px">Priority</Th>
                    <Th>Calendar Connection</Th>
                    <Th w="100px">Active</Th>
                    <Th w="60px"></Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {members.map((m) => {
                    const conn = availableConnections.find((c) => c.id === m.link_tool_user_id);
                    return (
                      <Tr key={m.id}>
                        <Td>
                          <Input
                            size="sm"
                            type="number"
                            value={m.priority}
                            onChange={(e) => {
                              const v = parseInt(e.target.value || "0", 10);
                              setMembers(members.map((x) => x.id === m.id ? { ...x, priority: v } : x));
                            }}
                            onBlur={(e) => {
                              const v = parseInt(e.target.value || "0", 10);
                              if (v !== m.priority) updateMemberPriority(m, v);
                            }}
                            w="70px"
                            isDisabled={pool.strategy !== "priority"}
                          />
                        </Td>
                        <Td>
                          {conn ? (
                            <Box>
                              <Text fontSize="sm" fontWeight="medium">{conn.name || "Unnamed"}</Text>
                              <Text fontSize="xs" color="gray.500">{conn.id}</Text>
                            </Box>
                          ) : (
                            <Badge colorScheme="orange">Connection not found</Badge>
                          )}
                        </Td>
                        <Td>
                          <Switch
                            isChecked={m.is_active}
                            onChange={() => toggleMemberActive(m)}
                            colorScheme="green"
                          />
                        </Td>
                        <Td>
                          <Tooltip label="Remove from pool">
                            <IconButton
                              size="xs"
                              colorScheme="red"
                              variant="outline"
                              icon={<MdDelete />}
                              aria-label="Remove"
                              onClick={() => removeMember(m)}
                            />
                          </Tooltip>
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
          )}
        </CardBody>
      </Card>
    </VStack>
  );
}
